/***
 * abstracts a file object into an attachment that can be uploaded to the boss service
 * options.uploadType : "image", "csv", or "message_center"
 * options.allowedFileType {array} - MIME types to support
 */
'use strict';
var SoaModel = require('bunsen/models/SoaModel');
var Deferred = require('simply-deferred').Deferred;
var bunsen = require('bunsen/libraries/bunsen');
var endpoints = require('dibs-endpoints');
var filesize = require('filesize');
var Qs = require('qs');
var assign = require('lodash.assign');
var last = require('lodash.last');
var flow = require('lodash.flow');
var FormData = require('form-data');

var Attachment = module.exports = SoaModel.extend({
    url : function (method) {
        if (this.uploadType === 'itemImage') {
            return endpoints.itemImageUpload();
        } else {
            return endpoints.cmsUpload(this.uploadType, method === 'create' ? this.get('dir') : undefined);
        }
    },
    sync : function (method, model, passedOptions) {
        // custom sync function so we can perform a multipart upload per the spec here: https://sites.google.com/a/1stdibs.com/wiki/home/engineering/services-documentation/boss-service/generic-file-uploader?pli=1
        var lastSave = Deferred();
        //always include user token in param
        var params = bunsen.getToken();
        var isDelete = method === 'delete';
        var defaultOptions;
        var options;
        var sank;

        if (passedOptions.itemId) {
            params.itemId = passedOptions.itemId;
        }

        if (isDelete) {
            params.keys = this.get('key');
            defaultOptions = {
                type: 'DELETE'
            };
        } else {
            defaultOptions = {
                xhr : function () { // make our own xhr so we can notify lastSave of its progress events
                    var xhr = new XMLHttpRequest();
                    xhr.upload.addEventListener("progress", function (e) {
                        this.set('upload-sent', e.loaded);
                        this.set('upload-total', e.total);
                        this.set('bitrate', e.bitrate);
                        lastSave.notify(e);
                    }.bind(this));
                    return xhr;
                }.bind(this),
                clump: false, // prevents autoclump from handing this
                type: 'POST',
                contentType: false, // forces multipart
                processData: false,
                data : new FormData(),
                cache : false
            };

            this.set('uploadStatus', 'pending');
        }

        defaultOptions.url = this.url(method) + "?" + Qs.stringify(params);
        options = assign(defaultOptions, passedOptions);

        sank = this.loaded.then(function (file) {
            var _sank;
            if (!isDelete && file) {
                defaultOptions.data.append('files', file, file.name);
            }
            _sank = Attachment.__super__.sync.call(this, method, model, options)
                .done(lastSave.resolve.bind(lastSave))
                .fail(lastSave.reject.bind(lastSave));
            lastSave.abort = _sank.abort.bind(_sank);
            return _sank;
        }.bind(this));

        //set lastSave promise if it has not been attached to the model
        if (!this.lastSave) {
            lastSave.always(flow(lastSave.state.bind(lastSave), this.set.bind(this, 'uploadStatus')));
            this.lastSave = lastSave;
        }
        return lastSave;
    },
    parseFile : function () {
        var file = this.get('file');
        var parsedFile = Deferred();
        var arrayBufferReader;
        var dataUrlReader;
        // TODO: fileBlob should be unset here, but is currently causing parseFile() to be called repeatedly due to 'change:fileBlob'
        //this.set('fileBlob', undefined);
        if (undefined === file) {
            return; // file not set yet
        }
        this.set('fileName', file.name);
        this.set('fileSize', file.size);
        this.set('displaySize', filesize(file.size));
        // update data-uri attribute
        if (!window.FileReader) {
            return;
        }
        arrayBufferReader = new window.FileReader();
        arrayBufferReader.onloadend = function () {
            var type = file.type;
            // workaround for chrome bug where mime type was not read correctly for xls files
            if (!type && last(file.name.split('.')) === 'xls') {
                type = 'application/vnd.ms-excel';
            }
            this.set('fileBlob', new Blob([arrayBufferReader.result], { type : type }));
            this.get('fileBlob').name = file.name;
            this.set('attachmentType', type);
            parsedFile.resolve(this.get('fileBlob'));
        }.bind(this);
        try {
            arrayBufferReader.readAsArrayBuffer(file);
        } catch (e) {
            parsedFile.reject();
            if (!(e instanceof TypeError)) {
                throw e;
            }
        }
        // need separate reader to avoid 'busy' error
        dataUrlReader = new window.FileReader();
        dataUrlReader.onloadend = function () {
            this.set('dataURI', dataUrlReader.result);
        }.bind(this);
        try {
            dataUrlReader.readAsDataURL(file);
        } catch (e) { /* do nothing if error occurs when reading data url */ }
        return parsedFile.promise();
    },
    parseAttachment: function () {
        this.set({
            'displayName': this.toDisplayName(this.get('sharedPath')),
            'displaySize': filesize(this.get('fileSize')),
            'thumbnailPath': this.getThumbnailPath(),
            'originalPath': this.isImageType() ? this.getOriginalPath() : this.getFullPath()
        });
    },
    toDisplayName: function (fileName) {
        return fileName && last(fileName.split('\\'));
    },
    getThumbnailPath: function () {
        return this.getFullPath('thumbnail');
    },
    getOriginalPath: function () {
        return this.getFullPath('original');
    },
    getFullPath: function (variantValue) {
        var sharedPath = this.get('sharedPath');

        if (variantValue) {
            sharedPath = this.replaceVariant(sharedPath, variantValue);
        }

        return [this.get('webPath'), this.get('webPathSuffix'), sharedPath].join('');
    },
    replaceVariant: function (path, value) {
        return path && path.replace('{variant}', value);
    },
    isImageType: function () {
        var type = this.get('attachmentType');
        return type && type.toLowerCase().indexOf('image') > -1;
    },
    initialize : function (attributes, options) {
        options = assign({
            allowedFileTypes: undefined,
            uploadType : 'image'
        }, options);
        this.uploadType = options.uploadType;

        //user uploaded file
        if (this.get('file')) {
            this.loaded = this.parseFile();
            //attachment coming from mc service
        } else if (this.get('sharedPath') && this.get('webPath')) {
            this.parseAttachment();
            this.loaded = Deferred().resolve().promise();
        }

        this.on('request', function (attachment, XHR) {
            this._xhr = XHR;
        });
    },
    // file failed to upload for some reason, abort request
    abort : function () {
        if (this._xhr.readyState !== 0 && this._xhr.readyState !== 4) {
            this._xhr.abort();
        }
    },
    defaults: {
        attachmentType: ''
    }
});



//////////////////
// WEBPACK FOOTER
// ./~/bunsen/models/Attachment.js
// module id = 171
// module chunks = 4 5 6 7 8 9 10 11 12 13 14 15 16 17 18 19 20 21 22 23 24 25 26 27 28 29 30 31 32 33 34 35 36 37 38 39 40 41 42 43 44 45 46 47 48 49 50 51 52 53 54 55